import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import ActivityModel from 'src/app/core/models/activity.model';
import { ActivityService } from 'src/app/core/services/activity.service';

declare var $: any;

/**
 * ActivitiesListComponent
 *
 * This component is responsible for managing Activity Types within the Material Management module.
 * It provides a user interface for administrators to view, create, update, and search for Activity types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all Activity types.
 * - Provide search functionality to filter Activity types by name.
 * - Manage the creation of new Activity types with validation.
 * - Handle updates to existing Activity types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update Activity types.
 * - **Real-time Search**: Filter list by Activity type name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures Activity type names follow specific patterns (alphanumeric, special chars).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all activity types from `ActivityTypesService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateActivityType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-activities-list
 * @example
 * <app-activities-list></app-activities-list>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-activities-list',
  templateUrl: './activities-list.component.html',
  styleUrls: ['./activities-list.component.scss']
})
export class ActivitiesListComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched activities from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {ActivityModel[]}
   * @default []
   */
  public activities: ActivityModel[] = [];

  /**
   * Array holding the currently displayed Activities list.
   * This list is populated by the BatchLoader (subset of activitiesList or filteredActivities).
   * @type {ActivityModel[]}
   * @default []
   */
  public activitiesList: ActivityModel[] = [];

  /**
   * Array holding transaction types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {ActivityModel[]}
   * @default []
   */
  public filteredActivities: ActivityModel[] = [];

  /**
   * Model for the current activity being added or edited.
   * Bound to the form for data manipulation.
   * @type {ActivityModel}
   */
  public activity: ActivityModel = new ActivityModel();

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Reactive form group for transaction type details (transaction_type, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to disable the submit button during async operations.
   * @type {boolean}
   */
  public submitButtonDisabled: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  enableDisablePageLoader: boolean;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for TransactionTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - transaction_type: Required, Min 3, Max 20, Pattern (alphanumeric, spaces, specific symbols).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toasterservice - Service for displaying toast notifications.
   * @service ToastrService
   * @param {ActivityService} activityService - Service for Activity API operations.
   * @service ActivityService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public toasterservice: ToastrService, public activityService: ActivityService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.activity = new ActivityModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[-+a-zA-Z ,()/]+$')]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all transaction types from the backend.
   * 5. Formats the 'updatedAt' date for each transaction type.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.activitiesList = [];
    this.activities = [];
    this.filteredActivities = [];
    this.isFiltering = false;
    this.search = '';
    this.sidenavaction.x_navigation();
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.activities = await this.activityService.getAllActivities();
      if (this.activities.length > 0) {
        this.activities.forEach(item => {
          item.updatedAt = JSON.parse(JSON.stringify(moment(item.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredActivities : this.activities,
      (updatedList) => {
        this.activitiesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.activityService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Filters the transaction type list based on the search query.
   *
   * - Filters by transaction_type name (case-insensitive).
   * - Updates `filteredTransactionTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredTransactionTypes`, `transactionTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.activities.filter(route =>
        route.name?.toLowerCase().includes(query)
      )
      : this.activities;
    this.filteredActivities = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.activitiesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.submitButtonDisabled = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.showForm = false;
    this.activity = new ActivityModel();
  }

  /**
   * Handles the submission of the Activity form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateTransactionType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateActivity() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.submitButtonDisabled = true;
      if (this.submitButton) {
        await this.activityService.create(formData);
        this.toasterservice.success('Activity created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateActivity = { ...formData };
        delete updateActivity.id;
        await this.activityService.updateOneByID(this.activity.id, updateActivity);
        this.toasterservice.success('Activity updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Retrieves a Activity by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getOneById
   * @param {string} id - The ID of the activity to edit.
   * @returns {void}
   */
  public getOneById(id: string) {
    this.submitButtonDisabled = false;
    this.submitButton = false;
    this.updateButton = true;
    this.showForm = true;
    const foundActivity = this.activitiesList.find(item => item.id === id);
    this.activity = foundActivity ? { ...foundActivity } : new ActivityModel(); // clone object
    if (this.activity) {
      this.registerForm.patchValue({
        name: this.activity.name
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
